import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ErrorResponse } from 'legacy/common/utils/error';
import {
  IncomingCreateEventStandardProductDto,
  IncomingCreateEventBundleProductDto,
  OutgoingEventProductDto,
  OutgoingEventProductsDto,
  IncomingReorderProductsDto,
  IncomingUpdateEventStandardProductDto,
  IncomingUpdateEventBundleProductDto
} from 'api/dtos/events/products';
import {
  createEventStandardProduct,
  createEventBundleProduct,
  getEventProducts,
  getEventProduct,
  updateEventStandardProduct,
  updateEventBundleProduct,
  deleteEventProduct,
  reorderEventProducts,
  getUnauthenticatedEventProducts
} from 'api/services/events/products';

// Query keys
export const eventProductsKeys = {
  all: ['eventProducts'] as const,
  lists: (eventId: string) => ['eventProducts', { eventId }] as const,
  detail: (eventId: string, productId: string) =>
    ['eventProducts', { eventId, productId }] as const,
  unauthenticated: (eventId: string) =>
    ['unauthenticatedEventProducts', { eventId }] as const
};

// Queries
export function useGetEventProducts(
  { eventId }: { eventId: string },
  options?: { enabled?: boolean }
) {
  return useQuery<OutgoingEventProductsDto, ErrorResponse>(
    eventProductsKeys.lists(eventId),
    () => getEventProducts(eventId),
    {
      enabled: eventId ? options?.enabled ?? true : false
    }
  );
}

export function useGetEventProduct({
  eventId,
  productId
}: {
  eventId: string;
  productId: string;
}) {
  return useQuery<OutgoingEventProductDto, ErrorResponse>(
    eventProductsKeys.detail(eventId, productId),
    () => getEventProduct({ eventId, productId })
  );
}

export function useGetUnauthenticatedEventProducts(
  { eventId }: { eventId: string },
  options?: { enabled?: boolean }
) {
  return useQuery<OutgoingEventProductsDto, ErrorResponse>(
    eventProductsKeys.unauthenticated(eventId),
    () => getUnauthenticatedEventProducts(eventId),
    {
      enabled: eventId ? options?.enabled ?? true : false
    }
  );
}

// Mutations
export function useCreateEventStandardProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingEventProductDto,
    ErrorResponse,
    {
      eventId: string;
      product: IncomingCreateEventStandardProductDto;
    }
  >(createEventStandardProduct, {
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries(eventProductsKeys.lists(eventId));
    }
  });
}

export function useCreateEventBundleProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingEventProductDto,
    ErrorResponse,
    {
      eventId: string;
      product: IncomingCreateEventBundleProductDto;
    }
  >(createEventBundleProduct, {
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries(eventProductsKeys.lists(eventId));
    }
  });
}

export function useUpdateEventStandardProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingEventProductDto,
    ErrorResponse,
    {
      eventId: string;
      productId: string;
      product: IncomingUpdateEventStandardProductDto;
    }
  >(updateEventStandardProduct, {
    onSuccess: (_, { eventId, productId }) => {
      queryClient.invalidateQueries(eventProductsKeys.lists(eventId));
      queryClient.invalidateQueries(
        eventProductsKeys.detail(eventId, productId)
      );
      queryClient.invalidateQueries(eventProductsKeys.unauthenticated(eventId));
    }
  });
}

export function useUpdateEventBundleProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    OutgoingEventProductDto,
    ErrorResponse,
    {
      eventId: string;
      productId: string;
      product: IncomingUpdateEventBundleProductDto;
    }
  >(updateEventBundleProduct, {
    onSuccess: (_, { eventId, productId }) => {
      queryClient.invalidateQueries(eventProductsKeys.lists(eventId));
      queryClient.invalidateQueries(
        eventProductsKeys.detail(eventId, productId)
      );
      queryClient.invalidateQueries(eventProductsKeys.unauthenticated(eventId));
    }
  });
}

export function useDeleteEventProduct() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ErrorResponse,
    {
      eventId: string;
      productId: string;
    }
  >(deleteEventProduct, {
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries(eventProductsKeys.lists(eventId));
      queryClient.invalidateQueries(eventProductsKeys.unauthenticated(eventId));
    }
  });
}

export function useReorderEventProducts() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ErrorResponse,
    {
      eventId: string;
      data: IncomingReorderProductsDto;
    }
  >(reorderEventProducts, {
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries(eventProductsKeys.lists(eventId));
      queryClient.invalidateQueries(eventProductsKeys.unauthenticated(eventId));
    }
  });
}
